import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Mail, RefreshCw, LogOut, CheckCircle } from 'lucide-react';

const VerifyPending = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [checking, setChecking] = useState(false);

  // Redirect if user is already verified
  useEffect(() => {
    if (user?.is_verified !== false) {
      navigate('/onboarding', { replace: true });
    }
  }, [user, navigate]);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`);
      setResent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      const updatedUser = await refreshUser();
      if (updatedUser && updatedUser.is_verified) {
        toast.success('Email verified! Redirecting...');
        navigate('/', { replace: true });
      } else {
        toast.error('Email not yet verified. Please check your inbox.');
      }
    } catch (error) {
      toast.error('Failed to check verification status');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border shadow-xl text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold font-heading mb-4">
            Verify Your Email
          </h1>

          {/* Description */}
          <p className="text-muted-foreground mb-2">
            We've sent a verification link to:
          </p>
          <p className="font-semibold text-lg mb-6">
            {user?.email}
          </p>

          {/* Instructions */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-muted-foreground">
              <strong>Please check your email</strong> and click the verification link to activate your account.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Don't forget to check your <strong>spam/junk folder</strong>!
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {resent ? (
              <div className="flex items-center justify-center gap-2 text-green-600 py-3">
                <CheckCircle className="w-5 h-5" />
                <span>Verification email sent!</span>
              </div>
            ) : (
              <Button
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full h-12 rounded-full font-bold"
                variant="outline"
              >
                {resending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            )}

            <Button
              onClick={handleCheckVerification}
              disabled={checking}
              className="w-full h-12 rounded-full font-bold bg-primary"
            >
              {checking ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  I've Verified My Email
                </>
              )}
            </Button>

            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full h-12 rounded-full font-bold text-muted-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out & Use Different Email
            </Button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Having trouble? Contact us at{' '}
          <a href="mailto:support@expatssphere.com" className="text-primary hover:underline">
            support@expatssphere.com
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default VerifyPending;
